import { useNavigate, useParams } from 'react-router-dom';
import { useAgency } from '../lib/AgencyContext';
import { useAuth } from '../lib/AuthContext';
import { useI18n } from '../lib/i18n';
import { SUPPORT, isSupportOnline, openAssistant } from '../lib/support';
import Icon from './Icon';

// ============================================================
// POMOĆ I PODRŠKA — /podrska, /podrska/tehnicka, /podrska/knjigovodstvena
// ============================================================

export default function SupportPage() {
  const { tip } = useParams();
  const navigate = useNavigate();
  const { tr } = useI18n();
  const { user } = useAuth();
  const { activeAgency } = useAgency();
  const online = isSupportOnline();

  const kind = tip === 'tehnicka' ? 'tech' : tip === 'knjigovodstvena' ? 'acc' : null;
  const title = kind === 'tech'
    ? tr('Tehnička podrška', 'Technical support', 'Техническая поддержка')
    : kind === 'acc'
      ? tr('Knjigovodstvena podrška', 'Accounting support', 'Бухгалтерская поддержка')
      : tr('Pomoć i podrška', 'Help & support', 'Помощь и поддержка');

  const mailSubject = encodeURIComponent(`FinArt Studio — ${title}${activeAgency?.name ? ` (${activeAgency.name})` : ''}`);
  const mailBody = encodeURIComponent(`\n\n—\n${tr('Nalog', 'Account', 'Аккаунт')}: ${activeAgency?.name || ''}\nE-mail: ${user?.email || ''}`);

  const Card = ({ icon, label, sub, onClick, href, muted }) => {
    const inner = (
      <>
        <span style={{ ...styles.cardIcon, ...(muted ? styles.cardIconMuted : {}) }}><Icon name={icon} size={20} /></span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={styles.cardLabel}>{label}</span>
          {sub && <span style={styles.cardSub}>{sub}</span>}
        </span>
        <Icon name="chevronRight" size={18} color="var(--lavender-700)" />
      </>
    );
    return href
      ? <a href={href} style={styles.card}>{inner}</a>
      : <button type="button" onClick={onClick} style={styles.card}>{inner}</button>;
  };

  const Hours = () => (
    <div style={{ ...styles.card, cursor: 'default' }}>
      <span style={styles.cardIcon}><Icon name="clock" size={20} /></span>
      <span style={{ flex: 1 }}>
        <span style={styles.cardLabel}>{tr('Radno vreme podrške', 'Support hours', 'Часы работы поддержки')}</span>
        <span style={styles.cardSub}>{tr('Ponedeljak – petak', 'Monday – Friday', 'Понедельник – пятница')}</span>
        <span style={styles.hours}>{String(SUPPORT.from).padStart(2, '0')}:00–{SUPPORT.to}:00</span>
      </span>
    </div>
  );

  return (
    <div style={styles.page}>
      {kind && (
        <button onClick={() => navigate('/podrska')} style={styles.back}>
          <Icon name="arrowLeft" size={16} /> {tr('Pomoć i podrška', 'Help & support', 'Помощь и поддержка')}
        </button>
      )}
      <div style={styles.hero}>
        <span style={styles.heroIcon}><Icon name={kind === 'acc' ? 'calculator' : 'headset'} size={34} /></span>
        <h1 style={styles.title}>{title}</h1>
        <span style={styles.status}>
          <span style={{ ...styles.statusDot, background: online ? 'var(--forest-500)' : 'var(--red-600)' }} />
          {online ? 'Online' : 'Offline'}
        </span>
      </div>

      {!kind && (
        <>
          <div style={styles.question}>{tr('Kako možemo da ti pomognemo?', 'How can we help you?', 'Чем мы можем помочь?')}</div>
          <div style={styles.list}>
            <Card icon="headset" label={tr('Tehnička podrška', 'Technical support', 'Техническая поддержка')}
              sub={tr('Pomoć oko aplikacije', 'Help with the app', 'Помощь с приложением')} onClick={() => navigate('/podrska/tehnicka')} />
            <Card icon="calculator" label={tr('Knjigovodstvena podrška', 'Accounting support', 'Бухгалтерская поддержка')}
              sub={tr('Knjigovodstvene usluge, posebno se naplaćuju', 'Accounting services, charged separately', 'Бухгалтерские услуги, оплачиваются отдельно')} onClick={() => navigate('/podrska/knjigovodstvena')} />
            <Hours />
          </div>
        </>
      )}

      {kind && (
        <>
          {kind === 'acc' && (
            <div style={styles.paidNote}>
              <Icon name="star" size={16} />
              {tr(
                'Knjigovodstvene usluge se naplaćuju posebno. Javi nam se i poslaćemo ti ponudu.',
                'Accounting services are charged separately. Contact us and we will send you an offer.',
                'Бухгалтерские услуги оплачиваются отдельно. Свяжитесь с нами, и мы пришлём предложение.',
              )}
            </div>
          )}
          <div style={styles.question}>{tr('Kako želiš da nas kontaktiraš?', 'How would you like to contact us?', 'Как вы хотите с нами связаться?')}</div>
          <div style={styles.list}>
            {kind === 'tech' && (
              <Card icon="message" label={tr('Chat podrška', 'Chat support', 'Чат поддержки')}
                sub={tr('AI asistent, odgovara odmah, 24/7', 'AI assistant, instant answers, 24/7', 'AI-ассистент, мгновенные ответы, 24/7')} onClick={openAssistant} />
            )}
            <Card icon="phone" label={tr('Pozovi nas', 'Call us', 'Позвоните нам')} sub={SUPPORT.phoneDisplay} href={`tel:${SUPPORT.phone}`} />
            <Card icon="mail" label={tr('Pošalji e-mail', 'Send an email', 'Написать на e-mail')} sub={SUPPORT.email}
              href={`mailto:${SUPPORT.email}?subject=${mailSubject}&body=${mailBody}`} />
            <Hours />
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  page: { maxWidth: 620, margin: '0 auto', paddingBottom: 40 },
  back: { display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'none', color: 'var(--teal-700)', fontWeight: 700, fontSize: 14, padding: 0, marginBottom: 8 },
  hero: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', margin: '24px 0 26px' },
  heroIcon: { width: 88, height: 88, borderRadius: '50%', background: 'var(--lavender-100)', color: 'var(--lavender-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 },
  title: { fontSize: 22, margin: '0 0 6px' },
  status: { display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 14, color: 'var(--ink-700)' },
  statusDot: { width: 9, height: 9, borderRadius: '50%' },
  question: { textAlign: 'center', fontWeight: 800, fontSize: 16, color: 'var(--ink-900)', marginBottom: 14 },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '16px 18px', cursor: 'pointer', textDecoration: 'none', color: 'inherit', boxSizing: 'border-box' },
  cardIcon: { width: 46, height: 46, borderRadius: '50%', background: 'var(--lavender-100)', color: 'var(--lavender-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardIconMuted: { background: 'var(--paper)', color: 'var(--ink-500)' },
  cardLabel: { display: 'block', fontSize: 16, fontWeight: 800, color: 'var(--ink-900)' },
  cardSub: { display: 'block', fontSize: 13, color: 'var(--ink-500)', marginTop: 2 },
  hours: { display: 'block', fontSize: 16, fontWeight: 800, color: 'var(--lavender-700)', marginTop: 4 },
  paidNote: { display: 'flex', alignItems: 'center', gap: 10, background: 'var(--gold-100)', color: 'var(--gold-600)', borderRadius: 8, padding: '12px 14px', fontSize: 13.5, marginBottom: 18 },
};
